import type { ReactNode } from 'react'
import { partialMatchKey, QueryClient } from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { CornerLayoutProvider } from '@joyful/ui/core'
import {
  buildApiQueryKey,
  buildJoyfulAppTelemetry,
  createJoyfulApiClient,
  getImpersonatedRole,
  initializeJoyfulAppTelemetry,
  JoyfulWebAppProvider,
} from '@joyful/ui/app'
import { env } from '@/env'

const localStoragePersister = createAsyncStoragePersister({
  key: 'joyful-query-cache',
  storage: window.localStorage,
})

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      throwOnError: false,
    },
    queries: {
      gcTime: 5 * 60 * 1000,
      retry: 2,
      staleTime: 30 * 1000,
      throwOnError: false,
    },
  },
})

interface AppProviderProps {
  children: ReactNode
}

const AnnouncementsQueryKey = buildApiQueryKey('/{org_slug}/announcements')

const joyfulApp = {
  api: createJoyfulApiClient({
    baseUrl: env.VITE_API_SERVER_URL,
    getImpersonatedRole,
    getToken: () => {
      return window.Clerk?.session?.getToken() ?? Promise.resolve(null)
    },
  }),
  appUrls: {
    apiBaseUrl: env.VITE_API_SERVER_URL,
    navbarAppOrigins: {
      app: env.VITE_JOYFUL_APP_WEB_ORIGIN,
      bpo: env.VITE_JOYFUL_BPO_WEB_ORIGIN,
      internal: env.VITE_JOYFUL_INTERNAL_WEB_ORIGIN,
      workspace: env.VITE_JOYFUL_WORKSPACE_WEB_ORIGIN,
    },
  },
  environment: env.VITE_JOYFUL_ENV,
  telemetry: buildJoyfulAppTelemetry({
    app: 'app',
    appSurface: 'app',
    commitSha: env.VITE_COMMIT_SHA,
    datadogApplicationId: env.VITE_DATADOG_APPLICATION_ID,
    datadogClientToken: env.VITE_DATADOG_CLIENT_TOKEN,
    datadogService: env.VITE_DATADOG_SERVICE,
    datadogSite: env.VITE_DATADOG_SITE,
  }),
}

initializeJoyfulAppTelemetry(joyfulApp)

export function AppProvider({ children }: AppProviderProps) {
  return (
    <JoyfulWebAppProvider
      app={joyfulApp}
      clerkPublishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}
      renderQueryProvider={(appProvider) => {
        return (
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              dehydrateOptions: {
                shouldDehydrateQuery: (query: {
                  queryKey: ReadonlyArray<unknown>
                  state: { status: string }
                }) => {
                  return (
                    query.state.status === 'success' &&
                    partialMatchKey(query.queryKey, AnnouncementsQueryKey)
                  )
                },
              },
              maxAge: 7 * 24 * 60 * 60 * 1000,
              persister: localStoragePersister,
            }}
          >
            {appProvider}
          </PersistQueryClientProvider>
        )
      }}
    >
      <CornerLayoutProvider>{children}</CornerLayoutProvider>
    </JoyfulWebAppProvider>
  )
}
